<template>
  <b-dropdown
    v-if="selectedInterval"
    variant="link"
    class="timeline-dropdown"
    :toggle-class="
      'd-flex align-items-center justify-content-between timeline-button ' +
        (isActiveInterval(selectedInterval) ? 'active' : '')
    "
    menu-class="menu-shadow timeline-dropdown-menu"
    no-caret
    :disabled="!intervals"
  >
    <template slot="button-content">
      <div>
        <div class="d-flex align-items-center text-medium timeline-field" id="timeline-field">
          <div
            id="timeline-pie"
            class="d-flex align-items-center timeline-pie"
            v-if="selectedInterval.filterIcon && isActiveInterval(selectedInterval)"
          >
            <template v-if="selectedInterval.filterIcon === 'timeline-pie'">
              <timeline-pie :completedPercent="getTimelineCompletedPercent(selectedInterval)" />
              <b-popover
                target="timeline-pie"
                triggers="hover"
                placement="bottom"
                delay="500"
                offset="0"
              >
                {{ getDaysLeftInTimeline(selectedInterval) }} days left in current timeline
              </b-popover>
            </template>
            <template v-else>
              <img
                :src="require(`@/assets/${selectedInterval.filterIcon}.svg`)"
                style="width: 16px; height: 16px;"
              />
            </template>
          </div>
          <span class="timeline-name">{{ selectedInterval.name }}</span>
          <transition name="fade">
            <div class="timeline-info-popover" v-if="showInactiveTimelineInfoPopover" @click.stop>
              <div class="title text-normal bold">
                You are in {{ selectedTimelineInfo.isPast ? 'an older' : 'a future' }} timeline
              </div>
              <div class="body text-medium mt-2">
                You’re looking at {{ selectedTimelineInfo.isPast ? 'older' : 'future' }} goals,
                change your timeline to see currently active goals
              </div>
              <b-button
                class="px-4 py-1 mt-3"
                variant="primary"
                @click="acknowledgeInactiveTimeline"
                pill
                >Got it</b-button
              >
            </div>
          </transition>
        </div>
      </div>
      <i class="caret-down-icon"></i>
    </template>
    <template v-for="interval in intervals">
      <b-dropdown-divider :key="interval.id + '-divider'" v-if="interval.id === 'past'" />
      <b-dropdown-item :key="interval.id" @click="onTimelineChange(interval)">
        <div class="d-flex paragraph-14">
          <div class="pie-container">
            <template v-if="isActiveInterval(interval)">
              <timeline-pie
                v-if="interval.filterIcon === 'timeline-pie'"
                :completedPercent="getTimelineCompletedPercent(interval)"
              />
              <img
                v-else-if="interval.filterIcon"
                :src="require(`@/assets/${interval.filterIcon}.svg`)"
                style="width: 16px; height: 16px;"
              />
            </template>
          </div>
          <div class="flex-grow-1 text-wrap">
            <div class="text-medium">{{ interval.name }}</div>
            <div class="text-small text-secondary pt-1">{{ interval.description }}</div>
          </div>
        </div>
      </b-dropdown-item>
    </template>
  </b-dropdown>
</template>

<script>
import TimelinePie from '@/components/TimelinePie'
import {
  getDaysLeftInTimeline,
  getTimelineCompletedPercent,
  isActiveInterval,
  nowInterval,
  setDefaultIntervalId
} from '@/interval'
import { BButton, BDropdown, BDropdownItem, BDropdownDivider, BPopover } from 'bootstrap-vue'

/**
 * This dropdown gives capability to change the interval
 * props:
 *  - intervals
 *      - This is an array of intervals containing name, description, start, end
 *  - selectedInterval
 *      - This is selected interval object. Should contain - name, start, end
 *
 * emits
 *  - change
 *    - This event is emitted when new value is selected from dropdown
 */
export default {
  name: 'TimelineDropdown',
  props: ['intervals', 'selectedInterval', 'showInactiveTimelineInfo', 'teamId'],
  components: {
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    TimelinePie,
    BButton,
    BPopover
  },
  data() {
    const selectedTimelineInfo = this.getSelectedTimelineInfo()
    const store = this.$store.state
    return {
      timelineChanged: false,
      showInactiveTimelineInfoPopover:
        !store.userInteractions.inactiveTimelineAcknowledged &&
        this.showInactiveTimelineInfo &&
        !selectedTimelineInfo.isActive
    }
  },
  computed: {
    selectedTimelineInfo() {
      return this.getSelectedTimelineInfo()
    }
  },
  methods: {
    acknowledgeInactiveTimeline() {
      this.$store.commit('SET_USER_INTERACTION', { inactiveTimelineAcknowledged: true })
      this.showInactiveTimelineInfoPopover = false
    },
    onTimelineChange(interval) {
      this.acknowledgeInactiveTimeline()
      this.$emit('change', interval)
    },
    getSelectedTimelineInfo() {
      const currentInterval = nowInterval()
      const currentTimelineStartDate = new Date(currentInterval.start).getTime()
      const currentTimelineEndDate = new Date(currentInterval.end).getTime()
      const selectedTimelineStartDate = new Date(this.selectedInterval?.start).getTime()
      const selectedTimelineEndDate = new Date(this.selectedInterval?.end).getTime()
      const isPast = selectedTimelineEndDate < currentTimelineStartDate
      const isFuture = selectedTimelineStartDate > currentTimelineEndDate
      return {
        isPast,
        isFuture,
        isActive: !(isPast || isFuture)
      }
    },
    isActiveInterval,
    getTimelineCompletedPercent,
    getDaysLeftInTimeline
  },
  mounted() {
    this.selectedInterval && setDefaultIntervalId(this.selectedInterval?.id)
  },
  watch: {
    teamId() {
      this.timelineChanged = false
      const store = this.$store.state
      const selectedTimelineInfo = this.getSelectedTimelineInfo()
      this.showInactiveTimelineInfoPopover =
        !store.userInteractions.inactiveTimelineAcknowledged &&
        this.showInactiveTimelineInfo &&
        !selectedTimelineInfo.isActive
    },
    isActiveTimelineSelected(current, previous) {
      if (current !== previous) {
        this.$store.commit('SET_USER_INTERACTION', { inactiveTimelineAcknowledged: false })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/animations/fade';

.timeline-dropdown /deep/ .dropdown-menu {
  width: 230px;
}

.timeline-dropdown /deep/ .dropdown-menu li {
  .dropdown-item {
    padding: 10px 14px;
    &:active {
      background-color: inherit;
      color: inherit;
    }
  }
}

/deep/ .timeline-button {
  text-decoration: none;
  color: $body-color;
  font-size: inherit;
  border: 0;
  border-radius: 20px;
  background-color: $quinary;
  padding: 0 12px 0 0;
  &.active {
    background-color: $brandSecondary;
  }
}

/deep/ .timeline-field {
  padding: 2px 0 2px 12px;
}

/deep/ .timeline-button.disabled,
/deep/ .timeline-button:disabled {
  opacity: 1;
  .caret-down-icon {
    display: none;
  }
}
/deep/ .timeline-button .caret-down-icon {
  margin-left: 10px;
}
.pie {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 1px solid $active;
  flex-shrink: 0;
}
.pie-container {
  padding-top: 2px;
  padding-right: 12px;
  width: 15px;
  box-sizing: content-box;
}
.timeline-pie {
  padding-right: 8px;
}

/* Timeline info popover style starts here*/
.timeline-info-popover {
  position: absolute;
  background: $primary;
  color: $documentColor;
  left: 0;
  transform: translate3d(calc(-100% - 15px), 0%, 0);
  top: -16px;
  padding: 24px;
  border-radius: 10px;
  width: 276px;
  white-space: initial;
  text-align: left;
  z-index: 1;
  &:after {
    content: ' ';
    position: absolute;
    border: 10px solid transparent;
    border-left-color: $primary;
    width: 20px;
    height: 20px;
    top: 19px;
    right: -19px;
  }
}

/* Timeline info popover style ends here*/
@include media-breakpoint-down(md) {
  /deep/ .timeline-button {
    padding: 5px 8px;
  }
  /deep/ .timeline-dropdown-menu {
    position: fixed !important;
    left: 50% !important;
    top: 90px !important;
    transform: translateX(-50%) !important;
    box-shadow: 0px 10px 30px #9f9f9f;
  }
}
</style>
